/**
 * Can the account that will actually be charged afford this upload?
 *
 * Every credit and storage figure the upload surfaces render comes from `billingStore` — *this*
 * user's wallet — so for a cross-billed library they describe the wrong account and are dropped
 * from the gates. `POST /billing/simulate/library/{libraryId}/ingest` prices against the payer,
 * and its 402 is what replaces them.
 *
 * **Only a decoded 402 blocks anything.** Every other outcome fails open. Inverting that is the
 * mistake this module is arranged against: a false `insufficient` tells someone their teammate
 * is out of credits and offers a remedy that cannot work, while a missed one costs a 402 at
 * ingest that `UploadManager` already surfaces.
 *
 * Endpoint contract, the disclosure in the 402 payload, and the history: see
 * `.claude/docs/cross-billed-uploads.md`.
 */

export type OwnerShortfall = 'credits' | 'storage' | 'unspecified';

/**
 * `unknown` covers both "not asked" and "asked, no answer" — neither is evidence about the
 * owner's wallet. `checking` is kept distinct from it so a caller can tell the two apart
 * (`dataframe-absence.md`); nothing renders it today. `insufficient` is the only state that
 * disables anything.
 */
export type OwnerAffordability =
  | { state: 'unknown' }
  | { state: 'checking' }
  | { state: 'affordable' }
  | { state: 'insufficient'; shortfall: OwnerShortfall };

/** Shared instances so `===` comparisons and effect deps stay stable. */
export const AFFORDABILITY_UNKNOWN: OwnerAffordability = { state: 'unknown' };
export const AFFORDABILITY_CHECKING: OwnerAffordability = { state: 'checking' };

export function ownerShortfallOf(affordability?: OwnerAffordability): OwnerShortfall | undefined {
  return affordability?.state === 'insufficient' ? affordability.shortfall : undefined;
}

/**
 * The single decision point behind every surface that can refuse a cross-billed upload. Both
 * halves have to move together: the verdict counts only when another account really is billed,
 * and the wording is always `ownerShortfallMessage`'s.
 */
export function ownerShortfallBlock(
  billedToAnotherAccount: boolean,
  affordability?: OwnerAffordability,
  ownerName?: string,
): { shortfall: OwnerShortfall; reason: string } | null {
  if (!billedToAnotherAccount) return null;
  const shortfall = ownerShortfallOf(affordability);
  if (!shortfall) return null;
  return { shortfall, reason: ownerShortfallMessage(shortfall, ownerName) };
}

/** Both served with HTTP 402. See `external.api.atlas` `src/routes/bouncers/monetization.ts`. */
export const NOT_ENOUGH_CREDITS_CODE = 'monetization.credits.insufficient';
export const NOT_ENOUGH_STORAGE_CODE = 'monetization.storage.insufficient';

/**
 * Duck-typed on `{ status, code }` rather than `instanceof ApiError`: importing
 * `@lib/network/api` pulls in `userStore` and a module-load `API_URL` assertion, which makes
 * this module unimportable from the standalone test runner.
 *
 * An unrecognised 402 still blocks — that is what the status code means — but under the vaguer
 * wording rather than guessing which resource ran out.
 */
export function affordabilityFromError(error: unknown): OwnerAffordability {
  if (typeof error !== 'object' || error === null) return AFFORDABILITY_UNKNOWN;
  const { status, code } = error as { status?: unknown; code?: unknown };
  if (status !== 402) return AFFORDABILITY_UNKNOWN;
  if (code === NOT_ENOUGH_CREDITS_CODE) return { state: 'insufficient', shortfall: 'credits' };
  if (code === NOT_ENOUGH_STORAGE_CODE) return { state: 'insufficient', shortfall: 'storage' };
  return { state: 'insufficient', shortfall: 'unspecified' };
}

/**
 * Every variant carries two things the reader's instinct gets wrong: whose account is short, and
 * that topping up their own won't help. The surfaces put an "Add credits" CTA in front of this
 * user everywhere else, so dropping either half sends them to buy credits that cannot unblock
 * the upload. Sibling of `billedToOwnerFailureNotice`, the post-failure form of this sentence.
 */
export function ownerShortfallMessage(shortfall: OwnerShortfall, ownerName?: string): string {
  const who = ownerName ?? 'the library owner';
  switch (shortfall) {
    case 'credits':
      return `Not enough credits on ${who}'s account to add this media. Adding credits to your own account won't help — they need to top up.`;
    case 'storage':
      return `Not enough storage on ${who}'s account to add this media. Upgrading your own plan won't help — they need to free up space or upgrade.`;
    case 'unspecified':
      return `Not enough credits or storage on ${who}'s account to add this media. Topping up your own account won't help.`;
  }
}

/** Compact form for the selection footers' owner badge, where the full sentence doesn't fit. */
export function ownerShortfallShortMessage(shortfall: OwnerShortfall, ownerName?: string): string {
  const who = ownerName ?? 'The library owner';
  switch (shortfall) {
    case 'credits':
      return `${who} doesn't have enough credits for this media.`;
    case 'storage':
      return `${who} doesn't have enough storage for this media.`;
    case 'unspecified':
      return `${who} doesn't have enough credits or storage.`;
  }
}
